import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { fetchInstagramAnonymouslyDemoData } from '@/lib/service/anonymously';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { getRevalidateTime, SHOW_ISR_DEBUG_PANEL, SITE_HOST, ISR_ENV } from '@/shared/isr-config';
import ISRDebugPanel from '@/components/Common/ISRDebugPanel';
import { page3FaqJsonLd } from '@/lib/jsonld';
import { getFaqQuestions } from '@/components/view-instagram-anonymously/FAQContent/FaqData';
import { AllSupportLanguages } from '@/shared/language';

const AnonymouslyContent = dynamic(() => import('@/components/view-instagram-anonymously'), {
  ssr: true,
});

const PAGE_PATH = '/anonymous-instagram-story-viewer';

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;
  // 1. 并行请求数据，添加 .catch() 防止构建失败
  const [localeRes, tdkRes, demoResRaw] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(PAGE_PATH, locale),
    fetchInstagramAnonymouslyDemoData(null, locale).catch((): null => null),
  ]);

  // 2. 语言 Fallback：当前语言无数据时请求英文
  let demoRes = demoResRaw;
  if (!demoRes?.data && locale !== 'en') {
    demoRes = await fetchInstagramAnonymouslyDemoData(null, 'en').catch((): null => null);
  }

  const siteUrl = locale !== 'en' ? `https://${SITE_HOST}/${locale}` : `https://${SITE_HOST}`;

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  // 创建一个简单的翻译函数，用于在服务端获取FAQ数据
  const t = (key: string) => tempData[key] || key;

  // 获取FAQ问题数据用于faqJsonLd
  const faqQuestions = getFaqQuestions(t);

  const jsonLd = page3FaqJsonLd(
    {
      title: 'Anonymous Instagram Story Viewer',
      desc: tdkRes?.data?.description ?? tempData?.head_Anonymously_Meta_Desc ?? null,
      url: canonicalUrl(SITE_HOST, PAGE_PATH, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    { url: siteUrl },
    faqQuestions, // 正确传递FAQ问题数组
    {
      name: 'Anonymous Instagram Story Viewer — DolphinRadar',
      url: 'https://www.dolphinradar.com/anonymous-instagram-story-viewer',
      applicationCategory: 'Instagram Story Viewer',
      description: 'Watch and download Instagram Stories from public accounts anonymously.',
      featureList: [
        'Story Viewer',
        'Story Downloader',
        'Post Viewer',
        'Post Downloader',
        'Highlights Viewer',
        'Highlights Downloader',
      ],
    }
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(SITE_HOST, PAGE_PATH, locale),
      locale,
      demoData: demoRes?.data ?? {},
      title: tdkRes?.data?.title ?? tempData?.head_Anonymous_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Anonymously_Meta_Desc ?? null,
      jsonLd,
      __isr_generated_at: Date.now(),
      __isr_revalidate: getRevalidateTime('anonymous-instagram-story-viewer'),
      __isr_page_name: 'anonymous-instagram-story-viewer',
      __isr_env: ISR_ENV,
    },
    revalidate: getRevalidateTime('anonymous-instagram-story-viewer'),
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return useMemo(
    () => (
      <>
        <AnonymouslyContent data={props} />
        {SHOW_ISR_DEBUG_PANEL && (
          <ISRDebugPanel
            pageName={props.__isr_page_name}
            generatedAt={props.__isr_generated_at}
            revalidate={props.__isr_revalidate}
            env={props.__isr_env}
          />
        )}
      </>
    ),
    [props]
  );
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={process.env.NEXT_APP_WEB_HOST + '/images/og/anonymous-instagram-story-viewer.png'}
          />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={process.env.NEXT_APP_WEB_HOST + '/images/og/anonymous-instagram-story-viewer.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
          {AllSupportLanguages.map((lang) => (
            <link
              key={lang.code}
              rel="alternate"
              hrefLang={lang.code}
              href={`https://www.dolphinradar.com/${lang.code}/anonymous-instagram-story-viewer`}
            />
          ))}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
